import React, { useEffect, useState } from 'react';
import { Menu, LogOut, Sun, Moon } from 'lucide-react';
import { Button } from '../ui/Button';
import { Tooltip } from '../ui/Tooltip';
import { useAuth } from '@/hooks/useAuth';
import { useApi } from '@/hooks/useApi';
// One-time migration from pre-rebrand key (2026-04-19). Safe to delete the
// helper + this import after all active users have been migrated (est. 3
// months based on session frequency).
import { migrateThemeKey } from './themeMigration.js';

interface HealthResponse {
  status: string;
  db: string;
  ts: string;
}

interface TopBarProps {
  onMenuToggle?: () => void;
  showMenuButton?: boolean;
  title?: string;
}

function formatUtc(d: Date): string {
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  const ss = String(d.getUTCSeconds()).padStart(2, '0');
  const yyyy = d.getUTCFullYear();
  const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mo}-${dd} ${hh}:${mm}:${ss} UTC`;
}

export function TopBar({
  onMenuToggle,
  showMenuButton = false,
  title,
}: TopBarProps) {
  const { isAuthed, logout } = useAuth();
  const { data: health, isError } = useApi<HealthResponse>(
    isAuthed ? '/api/health' : null
  );
  const [now, setNow] = useState(() => new Date());
  const [isDark, setIsDark] = useState(() => {
    if (typeof window === 'undefined') return false;
    // Run the one-time legacy-key migration, then read the canonical key.
    return migrateThemeKey() === 'dark';
  });

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.classList.toggle('dark', isDark);
    try {
      localStorage.setItem('caladan.theme', isDark ? 'dark' : 'light');
    } catch {
      /* ignore */
    }
  }, [isDark]);

  const serverOk = !isError && health?.status === 'ok';
  const dbOk = health?.db === 'ok';
  const allOk = serverOk && dbOk;

  return (
    <header
      className={[
        'chrome-dark',
        'flex items-center justify-between gap-4',
        'h-[var(--topbar-height)] px-5 shrink-0',
        'border-b border-[var(--border-subtle)]',
        'z-10',
      ].join(' ')}
    >
      {/* Left: menu + title */}
      <div className="flex items-center gap-4 min-w-0">
        {showMenuButton && (
          <Button
            variant="icon"
            onClick={onMenuToggle}
            aria-label="Toggle menu"
          >
            <Menu size={18} />
          </Button>
        )}
        {title && (
          <h1 className="text-[14px] font-[700] text-[var(--text-primary)] leading-none tracking-[-0.015em] truncate uppercase" style={{ letterSpacing: '0.05em' }}>
            {title}
          </h1>
        )}
      </div>

      {/* Right: live banner + status tags + auth */}
      <div className="flex items-center gap-4 shrink-0">
        {isAuthed && (
          <>
            {/* LIVE indicator */}
            <Tooltip
              content={allOk ? `Server OK · DB ${health?.db}` : 'Server unreachable'}
              placement="bottom"
            >
              <span className="flex items-center gap-1.5 cursor-default">
                <span
                  className="inline-block w-[7px] h-[7px] rounded-full"
                  style={{
                    background: allOk ? 'var(--accent-success)' : 'var(--accent-danger)',
                    boxShadow: allOk ? '0 0 6px var(--accent-success)' : 'none',
                    animation: allOk ? 'pulseDot 2s ease-in-out infinite' : 'none',
                  }}
                />
                <span
                  className="text-[10px] font-[600] uppercase"
                  style={{
                    letterSpacing: '0.12em',
                    color: allOk ? 'var(--accent-success)' : 'var(--accent-danger)',
                    fontFamily: 'var(--font-mono)',
                  }}
                >
                  {allOk ? 'LIVE' : 'OFFLINE'}
                </span>
              </span>
            </Tooltip>

            {/* All systems tag */}
            <span
              className="hidden md:inline-flex items-center gap-1.5 text-[10px] font-[500] uppercase"
              style={{
                letterSpacing: '0.12em',
                color: allOk ? 'var(--text-secondary)' : 'var(--accent-danger)',
                fontFamily: 'var(--font-mono)',
              }}
            >
              <span className="text-[var(--text-tertiary)]">SYS</span>
              {allOk ? 'OK' : 'ERR'}
            </span>

            {/* Live UTC clock */}
            <span
              className="hidden lg:inline-flex text-[10px] font-[500] tabular-nums"
              style={{
                color: 'var(--text-secondary)',
                fontFamily: 'var(--font-mono)',
                letterSpacing: '0.02em',
              }}
            >
              {formatUtc(now)}
            </span>
          </>
        )}

        {/* Theme toggle */}
        <Tooltip content={isDark ? 'Switch to light' : 'Switch to dark'} placement="bottom">
          <button
            onClick={() => setIsDark(d => !d)}
            aria-label="Toggle theme"
            className="flex items-center justify-center w-8 h-8 text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-overlay)] transition-colors"
            style={{ borderRadius: '2px' }}
          >
            {isDark ? <Sun size={14} /> : <Moon size={14} />}
          </button>
        </Tooltip>

        {/* Auth */}
        {isAuthed ? (
          <Tooltip content="Sign out" placement="bottom">
            <Button
              variant="ghost"
              size="sm"
              icon={<LogOut size={14} />}
              onClick={() => void logout()}
              aria-label="Sign out"
            >
              Sign out
            </Button>
          </Tooltip>
        ) : (
          <span className="text-[12px] text-[var(--text-tertiary)]">Not authenticated</span>
        )}
      </div>

      <style>{`
        @keyframes pulseDot {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </header>
  );
}
